import styled, { css } from "styled-components";
import {
  EmptyText as SharedEmptyText,
  getToneTokens,
  MetricCard,
  MetricLabel,
  MetricSub,
  MetricValue,
  PageActions,
  PageContainer,
  PageState as SharedPageState,
  PageTitle,
  PageTopBar,
  Section,
  SectionTitle,
} from "../../styles/shared";
import { mq } from "../../styles/canonical/breakpoints";
import {
  kpiFaixaCss,
  kpiTileCss,
} from "../../components/ListaCompacta/styles";

export const Wrapper = styled(PageContainer)`
  gap: 16px;
  /* REF-13: ocupa toda a largura disponível (sem o teto de 1500px que
     deixava faixas vazias nas laterais em telas largas). */
  max-width: none;
`;

export const PageState = styled(SharedPageState)``;

export const TopBar = styled(PageTopBar)``;

export const Titulo = styled(PageTitle)`
  font-weight: 600;
  @media ${mq.tablet} {
    font-size: 17px;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
`;

export const TopBarActions = styled(PageActions)``;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 12px;
`;

export const SummaryCard = styled(MetricCard)``;
export const SummaryLabel = styled(MetricLabel)``;
export const SummaryValue = styled(MetricValue)`
  font-weight: 600;
`;
export const SummarySub = styled(MetricSub)``;

export const Secao = styled(Section)``;
export const SecaoTitulo = styled(SectionTitle)``;

/**
 * TableWrapper LOCAL — sem overflow interno. Scroll vertical do <body>;
 * cabecalho da tabela usa position: sticky (var --app-header-height).
 */
export const TableWrapper = styled.div`
  width: 100%;
`;

/**
 * Tabela com suporte a densidade compact. Em compact:
 *  - table-layout: fixed + sem min-width (evita scroll horizontal)
 *  - Th/Td com padding reduzido e quebra automatica de texto
 * Th/Td herdam via seletor de pai [data-density="compact"] — evita
 * duplicar a prop em cada celula do JSX.
 */
export const Tabela = styled.table.attrs(({ $density }) => ({
  "data-density": $density === "compact" ? "compact" : undefined,
}))`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1100px;

  &[data-density="compact"] {
    font-size: 12px;
    table-layout: fixed;
    min-width: 0;
  }
`;

export const Th = styled.th`
  padding: 10px 12px;
  text-align: ${({ $center, $right }) =>
    $center ? "center" : $right ? "right" : "left"};
  /* Bugfix 2026-05-29: sticky com background opaco + top calculado
     para ficar ABAIXO do StickyHeaderSlot (~76px de altura). */
  position: sticky;
  top: calc(var(--app-header-height, 0px) + 76px);
  z-index: 5;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  white-space: nowrap;

  [data-density="compact"] & {
    padding: 6px 10px;
    font-size: 10px;
    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
  }
`;

export const Td = styled.td`
  padding: 10px 12px;
  text-align: ${({ $center, $right }) =>
    $center ? "center" : $right ? "right" : "left"};
  color: ${({ theme, $muted }) => ($muted ? theme.textSecondary : theme.textPrimary)};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  vertical-align: middle;
  white-space: ${({ $wrap }) => ($wrap ? "normal" : "nowrap")};
  ${({ $wrap }) =>
    $wrap &&
    `
    max-width: 320px;
    line-height: 1.45;
    word-break: break-word;
  `}

  [data-density="compact"] & {
    padding: 6px 10px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.25;
    max-width: none;
  }
`;

export const StatusBadge = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  /* Mockup GP (2026-07): badge nunca quebra ("Prazo/vencido" em 2 linhas)
     e leva o pontinho na cor do estado. */
  white-space: nowrap;

  &::before {
    content: "";
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    display: ${({ $tone = "default" }) => ($tone === "default" ? "none" : "block")};
  }
`;

export const ItemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

export const EmptyText = styled(SharedEmptyText)`
  padding: 18px 0;
`;

export const ClienteCelula = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ClienteNome = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ClienteAmbiente = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`;

/* =============================================================
 *  Redesign — 3 zonas, view switcher, rail, breakdown, drawer
 * ============================================================= */

export const StickyHeaderSlot = styled.div`
  /* Bugfix 2026-05-29: top:0 fazia ficar atras do header global do app
     (que eh position:fixed top:0 z-index:100). Agora gruda em
     var(--app-header-height)=56px (logo abaixo do header global).
     z-index 80 fica abaixo do header global (100) mas acima da tabela
     (5) — empilhamento visual correto. */
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 80;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  margin: -16px -16px 0;
  padding: 12px 16px;

  @media ${mq.mobile} {
    margin: -8px -8px 0;
    padding: 10px 12px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  @media ${mq.tablet} {
    gap: 8px;
  }
`;

export const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 0 1 auto;
  @media ${mq.tablet} {
    flex: 1 1 100%;
  }
`;

export const HeaderMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  @media ${mq.tablet} {
    flex-wrap: nowrap;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    gap: 6px;
  }
`;

export const HeaderMetaDot = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.55;
`;

export const HeaderControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: nowrap;

  @media ${mq.mobile} {
    margin-left: 0;
    width: 100%;
  }
  @media ${mq.tablet} {
    margin-left: 0;
    width: 100%;
  }
`;

export const SearchBox = styled.label`
  position: relative;
  flex: 1 1 280px;
  min-width: 200px;
  max-width: 360px;
  @media ${mq.tablet} {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.textSecondary};
  display: inline-flex;
  pointer-events: none;
`;

export const SearchInput = styled.input`
  width: 100%;
  min-height: 36px;
  padding: 7px 12px 7px 32px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }
`;

export const ViewSwitcher = styled.div`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  @media ${mq.tablet} {
    flex: 0 0 auto;
  }
`;

export const ViewToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  min-height: 36px;
  padding: 0 12px;
  border: 0;
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.textPrimary : theme.textSecondary)};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
  }

  &[aria-pressed="true"] {
    color: ${({ theme }) => theme.accent};
  }
  @media ${mq.tablet} {
    padding: 0 10px;
    white-space: nowrap;
  }
  /* Celular: só o ícone (o title/aria continua). */
  @media ${mq.mobile} {
    width: 40px;
    min-width: 40px;
    padding: 0;
    gap: 0;
    font-size: 0;
    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

export const ThreeZoneGrid = styled.div`
  display: grid;
  /* F8: rail recolhível — coluna estreita (40px) quando $colapsado. */
  grid-template-columns: ${({ $colapsado }) => ($colapsado ? "40px" : "320px")}
    minmax(0, 1fr);
  gap: ${({ $colapsado }) => ($colapsado ? "12px" : "18px")};
  align-items: start;

  /* minmax(0, 1fr) + min-width 0 nos filhos: sem isso a coluna cresce até
     o min-content de uma linha nowrap (chips/faixa) e a página inteira fica
     com 1500px de largura no celular. */
    @media ${mq.tablet} {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;

    > * {
      min-width: 0;
    }
  }
`;

export const ContextSticky = styled.aside`
  position: sticky;
  top: calc(var(--app-header-height, 0px) + 76px);
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100vh - var(--app-header-height, 0px) - 96px);
  overflow: auto;
  padding-right: 4px;

  @media ${mq.tablet} {
    position: static;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }
  @media ${mq.tablet} {
    gap: 8px;
  }
`;

/* div, NAO <main>: system.css da a todo <main> max-width + margin auto +
   padding. Dentro do grid, a margem automatica fazia a coluna encolher ate o
   conteudo — a lista ficava centralizada com faixas vazias dos dois lados. */
export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

/* -- Rail cards -- */

export const RailCard = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const RailTitle = styled.h2`
  margin: 0;
  font-size: 10px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const NextActionCard = styled.section`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      border-left-width: 4px;
      color: ${tokens.text};
    `;
  }}
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Celular/tablet: uma linha só — título à esquerda, botão à direita. */
  @media ${mq.tablet} {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 9px;
  }
`;

export const NextActionLabel = styled.div`
  font-size: 10px;
  @media ${mq.tablet} {
    display: none;
  }
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.85;
`;

export const NextActionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  @media ${mq.tablet} {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 12.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const NextActionBody = styled.div`
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.85;
  @media ${mq.tablet} {
    display: none;
  }
`;

export const NextActionButton = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
  @media ${mq.tablet} {
    flex: 0 0 auto;
    margin: 0;
    min-height: 32px;
    padding: 0 10px;
    white-space: nowrap;
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

export const KpiCard = styled.div`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    // Glass do mockup no tom default; tons de estado mantêm o fundo semântico.
    const bg =
      $tone === "default"
        ? `linear-gradient(155deg, ${theme.surfaceAlt}, ${theme.surface})`
        : tokens.background;
    return css`
      background: ${bg};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(13px) saturate(1.3);
  -webkit-backdrop-filter: blur(13px) saturate(1.3);

  /* Export (html2canvas ignora blur): gradiente glass viraria duas faixas
     de cor no JPG/PNG — achata para superficie solida so na captura. */
  body[data-export-mode="true"] & {
    background: ${({ theme, $tone = "default" }) =>
      $tone === "default"
        ? theme.surfaceAlt
        : getToneTokens(theme, $tone).background};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  ${kpiTileCss}
`;

export const KpiLabel = styled.div`
  font-size: 10px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.85;
`;

export const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
`;

/* -- Breakdown cards (clicáveis) -- */

export const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const BreakdownCard = styled.button`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      border-left: 4px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font: inherit;

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      cursor: default;
    `}

  &:hover:not(:disabled) {
    filter: brightness(0.97);
  }

  &[aria-pressed="true"] {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }
  ${kpiTileCss}
  @media ${mq.tablet} {
    min-height: 36px;
  }
`;

export const BreakdownLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  strong {
    font-size: 12px;
    font-weight: 700;
  }
  span {
    font-size: 10px;
    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
    opacity: 0.85;
    line-height: 1.3;
  }
`;

export const BreakdownCount = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
`;

/* -- Toolbar de filtros (Zona C) -- */

export const PanelToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 10px 12px;
  @media ${mq.tablet} {
    padding: 8px 10px;
    gap: 6px;
  }
`;

export const StatusChipsRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  @media ${mq.tablet} {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const StatusChip = styled.button`
  ${({ theme, $tone = "default", $active }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${$active ? tokens.background : "transparent"};
      border: 1px solid ${$active ? tokens.border : theme.border};
      color: ${$active ? tokens.text : theme.textSecondary};
    `;
  }}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 3px 10px;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
  @media ${mq.tablet} {
    flex: 0 0 auto;
    min-height: 36px;
    white-space: nowrap;
  }
`;

export const ChipCount = styled.span`
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

export const ChipDivider = styled.span`
  align-self: stretch;
  width: 1px;
  margin: 2px 4px;
  background: ${({ theme }) => theme.border};
`;

export const ToolbarMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  margin-left: auto;
  @media ${mq.tablet} {
    margin-left: 0;
  }
`;

export const ClearLink = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.accent};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;

  &:hover {
    text-decoration: underline;
  }
`;

export const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

/* -- Ambientes view (cards agrupados) -- */

export const AmbientesList = styled.div.attrs({
  "data-export-ambientes-grid": "true",
})`
  /* Grade responsiva: aproveita a largura (2-3 colunas em telas largas) em
     vez de uma coluna estreita. Na captura (data-export-mode) a regra global
     de styles/GlobalStyles.jsx fixa 2 colunas via [data-export-ambientes-grid]. */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 440px), 1fr));
  gap: 10px;
  align-items: start;
`;

export const LocalTag = styled.span`
  ${({ theme }) => {
    const tokens = getToneTokens(theme, "warning");
    return css`
      color: ${tokens.text};
      border: 1px dashed ${tokens.border};
    `;
  }}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 600;
  white-space: nowrap;
`;

export const AmbienteCard = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
  height: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.border};
  border-left: 4px solid
    ${({ theme, $tone = "default" }) =>
      $tone === "default" ? theme.border : getToneTokens(theme, $tone).border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  text-align: left;
  font: inherit;
  cursor: pointer;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const AmbienteCardHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
`;

export const AmbienteCardCliente = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const AmbienteCardNome = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`;

export const AmbienteCardMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

export const AmbienteCardItens = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px;
`;

export const ItemChip = styled.div`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      border-left: 3px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  padding: 6px 9px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  strong {
    font-size: 11px;
    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
    font-weight: 700;
  }
  span {
    font-size: 11px;
    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
    opacity: 0.9;
    overflow-wrap: anywhere;
    line-height: 1.3;
  }
`;

export const AmbienteCardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  white-space: nowrap;
`;

export const AmbienteCardValor = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

/* -- Drawer (slide from right) -- */

export const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  /* Bugfix 2026-05-29: era 90, mas header global eh 100 — drawer
     ficava atras. 200 garante que cobre o header tambem (UX padrao
     de drawer/modal eh sobrepor toda a tela). */
  z-index: 200;
  background: rgba(10, 15, 25, 0.32);
  display: flex;
  justify-content: flex-end;
`;

export const DrawerPanel = styled.aside`
  width: min(560px, 100vw);
  height: 100%;
  background: ${({ theme }) => theme.surface};
  border-left: 1px solid ${({ theme }) => theme.border};
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: terc-drawer-in 0.22s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes terc-drawer-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media ${mq.tablet} {
    width: 100vw;
    max-width: 100%;
    border-left: 0;
  }
`;

export const DrawerHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  @media ${mq.tablet} {
    padding: 10px 12px 10px 16px;
  }
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.25;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const DrawerSub = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;

  strong {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

export const DrawerClose = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
  }
  @media ${mq.tablet} {
    width: var(--tap-min, 44px);
    height: var(--tap-min, 44px);
  }
`;

export const DrawerBody = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media ${mq.tablet} {
    overflow-x: hidden;
    padding: 12px 16px 20px;
  }
`;

export const DrawerSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DrawerSectionTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const ChecklistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ChecklistRow = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.surface};

  ${({ $tone, theme }) => {
    const tokens = getToneTokens(theme, $tone || "default");
    return css`
      border-left: 4px solid ${tokens.border};
    `;
  }}

  ${({ $muted }) =>
    $muted &&
    css`
      opacity: 0.55;
    `}
  @media ${mq.tablet} {
    grid-template-columns: 28px minmax(0, 1fr) auto;
  }
`;

export const ChecklistIcon = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      border: 1px solid ${tokens.border};
    `;
  }}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

export const ChecklistContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.textSecondary};
    overflow-wrap: anywhere;
  }
`;

export const ChecklistStatus = styled.span`
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 600;
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
`;

export const ChecklistDisclaimer = styled.p`
  margin: 0;
  font-size: 11px;
  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
  line-height: 1.4;
`;

export const DateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  font-size: 12px;

  span:first-child {
    color: ${({ theme }) => theme.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  font-size: 12px;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  span:first-child {
    font-size: 10px;
    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.textSecondary};
  }
  span:last-child {
    font-size: 13px;
    color: ${({ theme }) => theme.textPrimary};
    overflow-wrap: anywhere;
  }
`;

/* -- Celular/tablet: faixa rolável de KPIs no lugar do rail lateral -- */

export const KpiFaixa = styled.div`
  display: flex;
  ${kpiFaixaCss}
  @media ${mq.tablet} {
    margin: 0;
    padding: 0;
  }
`;
